import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Shield,
  Briefcase,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import WizardStepIndicator from "./wizard/WizardStepIndicator";
import ClientInfoStep from "./wizard/ClientInfoStep";
import ComplianceStep from "./wizard/ComplianceStep";
import MatterSetupStep from "./wizard/MatterSetupStep";
import ReviewStep from "./wizard/ReviewStep";
import { intakeApi, usersApi, rateTablesApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Submission {
  _id: string;
  data: Record<string, any>;
  formId: any;
  status: string;
}

interface TeamUser {
  _id: string;
  name: string;
  email: string;
}

interface RateTable {
  _id: string;
  name: string;
}

export interface WizardData {
  // Step 1: Client
  type: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredLanguage: string;
  organizationName: string;
  organizationWebsite: string;
  addressLine1: string;
  addressCity: string;
  addressRegion: string;
  addressPostal: string;
  addressCountry: string;
  smsOptIn: boolean;
  tags: string[];

  // Step 2: Compliance
  conflictCheckStatus: string;
  conflictCheckNotes: string;
  amlStatus: string;
  amlProvider: string;
  amlRiskScore: string;
  onboardingNotes: string;

  // Step 3: Matter
  createMatter: boolean;
  matterTitle: string;
  practiceArea: string;
  billingType: string;
  flatFeeCents: string;
  rateTableId: string | null;
  responsibleLawyerId: string;
  assignedUserIds: string[];
  matterConflictSameAsClient: boolean;
  matterConflictStatus: string;
  matterConflictNotes: string;
}

interface ConvertLeadWizardProps {
  open: boolean;
  onClose: () => void;
  submission: Submission | null;
  onConverted: () => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

const STEPS = [
  { label: "Client", icon: User },
  { label: "Compliance", icon: Shield },
  { label: "Matter", icon: Briefcase },
  { label: "Review", icon: CheckCircle },
];

const INITIAL_DATA: WizardData = {
  type: "individual",
  displayName: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  preferredLanguage: "en",
  organizationName: "",
  organizationWebsite: "",
  addressLine1: "",
  addressCity: "",
  addressRegion: "",
  addressPostal: "",
  addressCountry: "CA",
  smsOptIn: false,
  tags: [],
  conflictCheckStatus: "not_started",
  conflictCheckNotes: "",
  amlStatus: "not_required",
  amlProvider: "",
  amlRiskScore: "",
  onboardingNotes: "",
  createMatter: true,
  matterTitle: "",
  practiceArea: "other",
  billingType: "hourly",
  flatFeeCents: "",
  rateTableId: null,
  responsibleLawyerId: "",
  assignedUserIds: [],
  matterConflictSameAsClient: true,
  matterConflictStatus: "not_started",
  matterConflictNotes: "",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractContactInfo(data: Record<string, any>) {
  const name =
    data.name ||
    data.full_name ||
    data.fullName ||
    [data.first_name || data.firstName, data.last_name || data.lastName]
      .filter(Boolean)
      .join(" ") ||
    "";
  const email = data.email || data.contact_email || "";
  const phone = data.phone || data.phone_number || data.contact_phone || "";
  return { name, email, phone };
}

// ─── Component ───────────────────────────────────────────────────────────────

const ConvertLeadWizard: React.FC<ConvertLeadWizardProps> = ({
  open,
  onClose,
  submission,
  onConverted,
}) => {
  const { userData } = useUserDataContext();
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>({ ...INITIAL_DATA, responsibleLawyerId: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back

  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [rateTables, setRateTables] = useState<RateTable[]>([]);

  // Fetch ancillary data on mount
  useEffect(() => {
    if (!open) return;
    usersApi.list({ limit: 100 }).then((res) => setTeamUsers(res.data?.data || [])).catch(() => {});
    rateTablesApi.list().then((res) => setRateTables(res.data?.data || res.data || [])).catch(() => {});
  }, [open]);

  // Pre-fill from submission data when opening
  useEffect(() => {
    if (!open || !submission) return;

    const contact = extractContactInfo(submission.data);
    const practiceArea = submission.data.practice_area || submission.data.practiceArea || "other";

    setWizardData({
      ...INITIAL_DATA,
      displayName: contact.name,
      firstName: submission.data.first_name || submission.data.firstName || "",
      lastName: submission.data.last_name || submission.data.lastName || "",
      email: contact.email,
      phone: contact.phone,
      practiceArea,
      responsibleLawyerId: userData._id || "",
      matterTitle: contact.name ? `${contact.name} - ${practiceArea.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}` : "",
    });
    setCurrentStep(0);
    setErrors({});
    setDirection(1);
  }, [open, submission]);

  const handleChange = useCallback((updates: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...updates }));
    // Clear errors for changed fields
    const clearedErrors = { ...errors };
    Object.keys(updates).forEach((key) => delete clearedErrors[key]);
    setErrors(clearedErrors);
  }, [errors]);

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!wizardData.displayName.trim()) {
        newErrors.displayName = "Display name is required";
      }
    }

    if (step === 1) {
      if (wizardData.conflictCheckStatus === "waived" && !wizardData.conflictCheckNotes.trim()) {
        newErrors.conflictCheckNotes = "Waiver reason is required";
      }
    }

    if (step === 2) {
      if (wizardData.createMatter) {
        if (!wizardData.matterTitle.trim()) {
          newErrors.matterTitle = "Matter title is required";
        }
        if (!wizardData.responsibleLawyerId) {
          newErrors.responsibleLawyerId = "Responsible lawyer is required";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goNext = () => {
    if (!validateStep(currentStep)) return;
    setDirection(1);
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setDirection(-1);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!submission) return;
    setSubmitting(true);

    try {
      const payload: Record<string, any> = {
        displayName: wizardData.displayName.trim(),
        type: wizardData.type,
        firstName: wizardData.firstName || null,
        lastName: wizardData.lastName || null,
        email: wizardData.email || null,
        phone: wizardData.phone || null,
        smsOptIn: wizardData.smsOptIn,
        preferredLanguage: wizardData.preferredLanguage,
        tags: wizardData.tags.length > 0 ? wizardData.tags : undefined,
      };

      // Organization
      if (wizardData.type === "organization") {
        payload.organization = {
          legalName: wizardData.organizationName || null,
          website: wizardData.organizationWebsite || null,
        };
      }

      // Address
      if (wizardData.addressLine1.trim()) {
        payload.address = {
          line1: wizardData.addressLine1,
          city: wizardData.addressCity || null,
          region: wizardData.addressRegion || null,
          postal: wizardData.addressPostal || null,
          country: wizardData.addressCountry || "CA",
        };
      }

      // Onboarding / compliance
      payload.onboarding = {
        conflictCheckStatus: wizardData.conflictCheckStatus,
        conflictCheckNotes: wizardData.conflictCheckNotes || null,
        amlStatus: wizardData.amlStatus,
        amlProvider: wizardData.amlProvider || null,
        amlRiskScore: wizardData.amlRiskScore ? Number(wizardData.amlRiskScore) : null,
        notes: wizardData.onboardingNotes || null,
      };

      // Matter
      payload.createMatter = wizardData.createMatter;
      if (wizardData.createMatter) {
        const conflictCheck = wizardData.matterConflictSameAsClient
          ? {
              status: wizardData.conflictCheckStatus,
              notes: wizardData.conflictCheckNotes || null,
            }
          : {
              status: wizardData.matterConflictStatus,
              notes: wizardData.matterConflictNotes || null,
            };

        payload.matter = {
          title: wizardData.matterTitle.trim(),
          practiceArea: wizardData.practiceArea,
          billingType: wizardData.billingType,
          responsibleLawyerId: wizardData.responsibleLawyerId,
          assignedUserIds: wizardData.assignedUserIds,
          conflictCheck,
          ...(wizardData.rateTableId ? { rateTableId: wizardData.rateTableId } : {}),
          ...(wizardData.billingType === "flat" && wizardData.flatFeeCents
            ? { flatFeeCents: Math.round(Number(wizardData.flatFeeCents) * 100) }
            : {}),
        };
      }

      await intakeApi.convertSubmission(submission._id, payload);
      onConverted();
    } catch (err: any) {
      console.error("Failed to convert lead:", err);
      setErrors({ _form: err.response?.data?.error || "Failed to convert lead" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <PremiumModal
      open={open && !!submission}
      onClose={onClose}
      title="Convert to Client"
      subtitle={`Step ${currentStep + 1} of ${STEPS.length}: ${STEPS[currentStep]?.label}`}
      icon={ArrowRight}
      size="lg"
      footer={
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
            {isLastStep ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
                style={{ backgroundColor: PRIMARY }}
                onMouseEnter={(e) => !submitting && (e.currentTarget.style.backgroundColor = HOVER)}
                onMouseLeave={(e) => !submitting && (e.currentTarget.style.backgroundColor = PRIMARY)}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Converting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Convert to Client
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors"
                style={{ backgroundColor: PRIMARY }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = HOVER)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = PRIMARY)}
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Step Indicator */}
      <div className="mb-6">
        <WizardStepIndicator currentStep={currentStep} steps={STEPS} />
      </div>

      {/* Form Error */}
      {errors._form && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          {errors._form}
        </div>
      )}

      {/* Step Content with animation */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: direction > 0 ? 30 : -30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction > 0 ? -30 : 30 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          {currentStep === 0 && (
            <ClientInfoStep data={wizardData} onChange={handleChange} errors={errors} />
          )}
          {currentStep === 1 && (
            <ComplianceStep data={wizardData} onChange={handleChange} errors={errors} />
          )}
          {currentStep === 2 && (
            <MatterSetupStep
              data={wizardData}
              onChange={handleChange}
              errors={errors}
              teamUsers={teamUsers}
              rateTables={rateTables}
            />
          )}
          {currentStep === 3 && (
            <ReviewStep data={wizardData} teamUsers={teamUsers} />
          )}
        </motion.div>
      </AnimatePresence>
    </PremiumModal>
  );
};

export default ConvertLeadWizard;
